import { useEffect, useState } from "react";
import { Button, DatePicker, Drawer, Flex, Form, Input, Radio, Select, Space } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { Channel, conversationsApi } from "@/services/conversations.api";
import { StoreOperator, storePopulationsApi } from "@/services/store-populations.api";
import { contactsApi } from "@/services/contacts.api";

type DatePreset = "all" | "today" | "yesterday" | "this_week" | "this_month" | "personalized";
type StatusSel = "open" | "closed" | "no_participants";

export interface AdvancedFilterValue {
  status?: "open" | "closed";
  channelId?: string;
  company?: string;
  participantUserId?: number;
  dateFrom?: string;
  dateTo?: string;
  /** #117: filtro por Contact group → el id del grupo (para UI) y sus miembros (CSV). */
  contactGroupId?: number;
  contactIds?: string;
  /** #117: "No participants" (sin operador asignado) = quickFilter no_operators. */
  noParticipants?: boolean;
}

function rangeFromPreset(preset: DatePreset, custom?: [Dayjs, Dayjs] | null): { dateFrom?: string; dateTo?: string } {
  switch (preset) {
    case "today":
      return { dateFrom: dayjs().startOf("day").toISOString() };
    case "yesterday":
      return {
        dateFrom: dayjs().subtract(1, "day").startOf("day").toISOString(),
        dateTo: dayjs().subtract(1, "day").endOf("day").toISOString(),
      };
    case "this_week":
      return { dateFrom: dayjs().startOf("week").toISOString() };
    case "this_month":
      return { dateFrom: dayjs().startOf("month").toISOString() };
    case "personalized":
      return custom
        ? { dateFrom: custom[0].startOf("day").toISOString(), dateTo: custom[1].endOf("day").toISOString() }
        : {};
    default:
      return {};
  }
}

/** Drawer "Filtros": Fecha / Estado / Canal / Participantes / Empresa / Grupo de contacto. */
export const AdvancedFilters = ({
  open,
  onClose,
  storeCode,
  value,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  storeCode: string;
  value: AdvancedFilterValue;
  onApply: (v: AdvancedFilterValue) => void;
}) => {
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [custom, setCustom] = useState<[Dayjs, Dayjs] | null>(null);
  const [statusSel, setStatusSel] = useState<StatusSel | undefined>(
    value.noParticipants ? "no_participants" : value.status,
  );
  const [channelId, setChannelId] = useState<string | undefined>(value.channelId);
  const [company, setCompany] = useState<string>(value.company ?? "");
  const [participantUserId, setParticipantUserId] = useState<number | undefined>(value.participantUserId);
  const [contactGroupId, setContactGroupId] = useState<number | undefined>(value.contactGroupId);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [operators, setOperators] = useState<StoreOperator[]>([]);
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    if (!open || !storeCode) return;
    conversationsApi.listChannels(storeCode).then(setChannels).catch(() => setChannels([]));
    storePopulationsApi.listOperators(storeCode).then(setOperators).catch(() => setOperators([]));
    contactsApi.listContactGroups(storeCode).then(setGroups).catch(() => setGroups([]));
  }, [open, storeCode]);

  async function apply() {
    const { dateFrom, dateTo } = rangeFromPreset(datePreset, custom);
    const noParticipants = statusSel === "no_participants";
    const status = statusSel === "open" || statusSel === "closed" ? statusSel : undefined;
    let contactIds: string | undefined;
    if (contactGroupId) {
      const ids = await contactsApi.groupContactIds(contactGroupId);
      contactIds = ids.length ? ids.join(",") : "-1";
    }
    onApply({
      status,
      channelId,
      company: company.trim() || undefined,
      participantUserId,
      dateFrom,
      dateTo,
      contactGroupId,
      contactIds,
      noParticipants,
    });
    onClose();
  }

  function clear() {
    setDatePreset("all");
    setCustom(null);
    setStatusSel(undefined);
    setChannelId(undefined);
    setCompany("");
    setParticipantUserId(undefined);
    setContactGroupId(undefined);
    onApply({});
    onClose();
  }

  return (
    <Drawer
      title="Filtros"
      placement="right"
      width={400}
      open={open}
      onClose={onClose}
      footer={
        <Flex justify="space-between">
          <Button danger onClick={clear}>Limpiar filtros</Button>
          <Button type="primary" icon={<FilterOutlined />} style={{ background: "#00733B" }} onClick={apply}>
            Filtrar
          </Button>
        </Flex>
      }
    >
      <Form layout="vertical">
        <Form.Item label="Fecha">
          <Radio.Group
            value={datePreset}
            onChange={(e) => setDatePreset(e.target.value)}
            optionType="button"
            buttonStyle="outline"
            size="small"
          >
            <Space wrap>
              <Radio.Button value="all">Todos</Radio.Button>
              <Radio.Button value="today">Hoy</Radio.Button>
              <Radio.Button value="yesterday">Ayer</Radio.Button>
              <Radio.Button value="this_week">Esta semana</Radio.Button>
              <Radio.Button value="this_month">Este mes</Radio.Button>
              <Radio.Button value="personalized">Personalizado</Radio.Button>
            </Space>
          </Radio.Group>
          {datePreset === "personalized" && (
            <DatePicker.RangePicker
              style={{ marginTop: 8, width: "100%" }}
              value={custom as any}
              onChange={(v) => setCustom(v as [Dayjs, Dayjs] | null)}
            />
          )}
        </Form.Item>

        <Form.Item label="Estado">
          <Radio.Group value={statusSel} onChange={(e) => setStatusSel(e.target.value)} optionType="button" buttonStyle="outline" size="small">
            <Space wrap>
              <Radio.Button value="open">
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#15744b", marginRight: 6, verticalAlign: "middle", position: "relative", top: -1 }} />Abierta
              </Radio.Button>
              <Radio.Button value="closed">
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", border: "1.5px solid #D9D9D9", background: "#fff", marginRight: 6, verticalAlign: "middle", position: "relative", top: -1 }} />Cerrada
              </Radio.Button>
              <Radio.Button value="no_participants">Sin participantes</Radio.Button>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item label="Canal">
          <Select
            allowClear
            placeholder="Todos los canales"
            value={channelId}
            onChange={setChannelId}
            options={channels.map((c) => ({ value: c.channelId, label: `${c.name} · ${c.identifier}` }))}
          />
        </Form.Item>

        <Form.Item label="Participantes">
          <Select
            allowClear
            placeholder="Todos los participantes"
            value={participantUserId}
            onChange={setParticipantUserId}
            options={operators.map((o) => ({ value: o.userId, label: o.name }))}
          />
        </Form.Item>

        <Form.Item label="Empresa">
          <Input placeholder="Buscar empresa" value={company} onChange={(e) => setCompany(e.target.value)} />
        </Form.Item>

        <Form.Item label="Grupo de contacto">
          <Select
            allowClear
            placeholder="Todos los grupos"
            value={contactGroupId}
            onChange={setContactGroupId}
            options={groups.map((g) => ({ value: g.id, label: g.name }))}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
